import { FormGroup, Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Predicate } from "@syncfusion/ej2-data";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import {
  AutoCompleteComponent,
  AutoCompleteModel,
} from "@syncfusion/ej2-react-dropdowns";
import classNames from "classnames";
import React from "react";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import {
  BaseFieldProps,
  AsyncODataDropdownFieldValue,
  FieldLabeledProps,
} from "../../types/fieldTypes";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { useAsyncODataDropdownField } from "./useAsyncODataDropdownField";
import { AsyncODataFieldProps } from "../types";

export interface AsyncODataDropdownFieldTableRenderingColumnProps {
  // The header text to display for the column.
  //
  // Default value: `textColumnName` (e.g., "label") if more than one value, otherwise "Please select a value"
  headerText?: string;

  // If specified, a custom renderer for the cell being displayed.
  //
  // Default renderer displays the value of the column in a `span` element.
  columnRenderer?: (columnValue: any, item: any) => React.ReactNode;
}

export interface AsyncODataDropdownFieldTableRenderProps {
  // The header renderer.
  //
  // Default renderer: bolded `headerText`
  headerRenderer?: (headerText: string) => React.ReactNode;

  // Determines whether to display a table header or not.
  //
  // Default value: `true`
  showHeader?: boolean;

  // The group renderer.
  //
  // Default renderer: `groupName`
  groupRenderer?: (groupName: string, groupData: any) => React.ReactNode;

  // The default renderer for the table cells.
  //
  // Default renderer displays the value of the column in a `span` element.
  columnRenderer?: (columnValue: any, item: any) => React.ReactNode;

  // Column names and their table rendering properties.
  //
  // By default, only the `textColumnName` is displayed in the table.
  columnsToDisplay?: {
    [key: string]: AsyncODataDropdownFieldTableRenderingColumnProps;
  };
}

export interface AsyncODataDropdownFieldCustomRenderProps {
  // JSX Renderer for the dropdown items.
  // Input is the data from the OData endpoint; output is the JSX to render.
  //
  // No default renderer.
  itemRenderer?: (item: any) => React.ReactNode;

  // JSX Renderer for the dropdown header.
  //
  // No default renderer.
  headerRenderer?: () => React.ReactNode;

  // JSX Renderer for the dropdown group header.

  // No default renderer.
  groupRenderer?: (groupName: string) => React.ReactNode;
}

export interface AsyncODataDropdownFieldProps
  extends BaseFieldProps<AsyncODataDropdownFieldValue>,
    Partial<AsyncODataFieldProps> {
  renderProps?:
    | AsyncODataDropdownFieldTableRenderProps
    | AsyncODataDropdownFieldCustomRenderProps;

  // The column name used to group the values in the dropdown.
  //
  // Must specify a value to group. There is no default value.
  groupByColumnName?: string;

  // If specified, applies a transform to the data before it is provided to the dropdown.
  //
  // In practice this is used to add a "category" column to the data to allow for grouping.
  transformData?: (data: any[]) => any[];

  // If specified, applies a predicate (where clause) to the OData query.
  //
  // Right now, none of our OData endpoints support "select" anyway (we return all columns)
  predicate?: Predicate;

  // The column name used to sort the values in the dropdown.
  //
  // Defaults to `textColumnName`
  sortByColumnName?: string;

  // Whether to sort the values in descending order.
  //
  // Defaults to `false` (ascending order)
  sortDescending?: boolean;

  // How many items to take.
  //
  // Defaults to `100`.
  take?: number;

  // If specified, no searching until the min length is reached.
  minLength?: number;

  // Use a custom factory to generate a predicate from a search string.
  //
  // Example: (searchText: string) => new Predicate("label", "contains", searchText)
  searchByPredicateFactory?: (searchText: string) => Predicate;

  // Props to label the field.
  labelProps?: FieldLabeledProps;

  // Please avoid using this and if you need something from autoCompleteSettings, extend this component instead.
  // If provided, these autoCompleteSettings will be merged into the SyncFusion props.
  autoCompleteSettings?: Omit<
    AutoCompleteModel,
    | "value"
    | "disabled"
    | "select"
    | "focus"
    | "onClick"
    | "change"
    | "filtering"
    | "created"
    | "actionFailure"
    | "actionComplete"
    | "dataBound"
    | "readonly"
    | "query"
    | "minLength"
  > &
    DefaultHtmlAttributes;

  hideSearchIcon?: boolean;
}

const AsyncODataDropdownField: React.FC<AsyncODataDropdownFieldProps> = (
  props
) => {
  const {
    autoCompleteRef,
    ready,
    setIsDataLoaded,
    localValue,
    autoCompleteSettings,
    handleValueChange,
  } = useAsyncODataDropdownField(props);

  return (
    <FormGroup
      className={classNames(
        `${
          props.isRequired && !localValue
            ? "has-required-background"
            : "ucl-async-odata-dropdown-field"
        }`
      )}
    >
      {props.label && (
        <FieldLabel
          labelName={props.label}
          tooltipDescription={props.description}
          fieldProps={{ ...props, value: localValue }}
          isRequired={props.isRequired}
          {...props.labelProps}
        />
      )}
      <div className="ucl-async-odata-dropdown-toolbar">
        {ready && autoCompleteSettings?.dataSource && (
          <div className="ucl-async-odata-dropdown-input-container">
            <AutoCompleteComponent
              ref={autoCompleteRef}
              id="ucl-async-odata-dropdown-search-list"
              className="ucl-async-odata-dropdown-input"
              onChange={(e: {
                target: { value: AsyncODataDropdownFieldValue };
              }) => {
                handleValueChange(e.target.value);
              }}
              readonly={props.disabled}
              placeholder="Type to search..."
              dataBound={() => {
                setIsDataLoaded(true);
              }}
              minLength={props.minLength}
              {...autoCompleteSettings}
            />
            {!props.hideSearchIcon && (
              <Icon icon={IconNames.Search} size={14} />
            )}
          </div>
        )}
      </div>
      {props.errorMessages && (
        <FormFieldErrorContainer errorMessages={props.errorMessages} />
      )}
    </FormGroup>
  );
};

export default AsyncODataDropdownField;
